import { useState, useCallback } from 'react';

interface FormState<T> {
  data: T | Partial<T>;
  isOpen: boolean;
  mode: 'create' | 'edit';
}

interface FormActions<T> {
  openCreate: () => void;
  openEdit: (item: T) => void;
  close: () => void;
  updateField: (field: keyof T, value: any) => void;
  updateData: (data: Partial<T>) => void;
  reset: () => void;
}

export const useForm = <T>(emptyData: Omit<T, 'id'>): FormState<T> & FormActions<T> => {
  const [data, setData] = useState<T | Partial<T>>(emptyData as T | Partial<T>);
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<'create' | 'edit'>('create');

  const openCreate = useCallback(() => {
    setData(emptyData as T | Partial<T>);
    setMode('create');
    setIsOpen(true);
  }, [emptyData]);

  const openEdit = useCallback((item: T) => {
    setData(item);
    setMode('edit');
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setData(emptyData as T | Partial<T>);
  }, [emptyData]);

  const updateField = useCallback((field: keyof T, value: any) => {
    setData(prev => ({ ...prev, [field]: value }));
  }, []);

  const updateData = useCallback((newData: Partial<T>) => {
    setData(prev => ({ ...prev, ...newData }));
  }, []);

  const reset = useCallback(() => {
    setData(emptyData as T | Partial<T>);
  }, [emptyData]);

  return {
    data,
    isOpen,
    mode,
    openCreate,
    openEdit,
    close,
    updateField,
    updateData,
    reset,
  };
};
